import React from 'react'

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'strike' | 'outlined';
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    fullWidthOnMobile?: boolean;
    badge?: React.ReactNode;
    badgeVariant?: 'primary' | 'error' | 'warning';
    fullWidth?: boolean;
}

const SmallButton = (props: Props) => {
    const {
        startIcon: startIcon,
        endIcon: endIcon,
        fullWidthOnMobile: fullWidthOn,
        badge: badge,
        badgeVariant: badgeVariant,
        fullWidth: fullWidth,
        ...buttonProps
    } = props;
    const variant = props.variant || 'primary';
    function leftPadding() {
        if (!props.startIcon) {
            return 'pl-[12px]'
        }
        return 'pl-[10px]'
    }

    function rightPadding() {
        if (props.badge) {
            return 'pr-[6px]'
        }
        if (!props.endIcon) {
            return 'pr-[12px]'
        }
        return 'pr-[6px]'
    }

    function getColorClassNames() {
        switch (variant) {
            case 'primary':
                return 'bg-primary-dark border-solid border-primary-dark border-[1px] text-white hover:bg-primary disabled:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed';
            case 'secondary':
                return 'bg-neutral-deep border-solid border-primary-dark border-[1px] text-primary-dark hover:bg-secondary-light disabled:bg-neutral-light disabled:opacity-50 disabled:cursor-not-allowed';
            case 'strike':
                return 'bg-strike border-solid border-strike border-[1px] text-white hover:bg-strike-light disabled:bg-strike-dark disabled:opacity-50 disabled:cursor-not-allowed';
            case 'outlined':
                return 'bg-white border-solid border-primary-dark border-[1px] text-primary-dark hover:bg-secondary-light disabled:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed';
            default:
                return 'bg-primary-dark text-white';
        }
    }

    function getBadgeClassNames() {
        if (props.badgeVariant === 'error') {
            return 'bg-error-bg text-error';
        }
        if (props.badgeVariant === 'warning') {
            return 'bg-warning-bg text-warning';
        }
        switch (variant) {
            case 'primary':
                return 'bg-neutral-deep border-solid border-primary-dark border-[1px] text-primary-dark';
            case 'outlined':
                return 'bg-neutral-deep border-solid border-primary-dark border-[1px] text-primary-dark';
            case 'secondary':
                return 'bg-neutral-deep border-solid border-primary-dark border-[1px] text-primary-dark';
            default:
                return 'bg-primary-light border-solid border-primary-light border-[1px] text-primary-dark';
        }
    }

    function buttonWidthClassNames() {
        if (props.fullWidth) {
            return 'w-full'
        }
        if (props.fullWidthOnMobile) {
            return "w-full md:w-fit"
        }
        return 'w-fit'
    }

    function contentWidthClassNames() {
        if (props.fullWidth) {
            return 'w-full'
        }
        if (props.fullWidthOnMobile) {
            return "w-full md:w-fit"
        }
        return 'w-fit'
    }

    return (
        <button
            {...buttonProps}
            className={`${leftPadding()} ${rightPadding()} ${buttonWidthClassNames()} h-min py-[1.5px] text-[16px] rounded-[12px] transition-all ${getColorClassNames()} flex-wrap`}
        >
            <div className="flex gap-1">
                {props.startIcon && (<div className="pt-1.5">
                    {props.startIcon}
                </div>)}
                <div className={`leading-[18px] py-[9px] font-semibold tracking-[-0.32px] ${contentWidthClassNames()}`}>
                    {props.children}
                </div>
                {props.endIcon && (<div className="pt-1">
                    {props.endIcon}
                </div>)}
                {props.badge && (
                    <div className={`flex items-center justify-center h-[28px] rounded-[10px] px-2 mt-1 ml-2 ${getBadgeClassNames()}`}>
                        {props.badge}
                    </div>
                )}
            </div>
        </button>
    )
}

export default SmallButton